import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  Eye,
  Printer,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';

import { Part } from '../../services/types';
import { Button } from '../ui';
import { DataTable, type ColumnDef } from '../DataTable';
import { apiService } from '../../services/apiService';
import {
  getCachedData,
  setCachedData,
  clearAllCachedDataFor,
} from '../../utils/pageCache';

const PartsSearch: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial state from URL params
  const urlSearch = searchParams.get('search') || '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const urlPageSize = parseInt(searchParams.get('pageSize') || '50', 10);
  const urlIncludeInactives = searchParams.get('includeInactives') === 'true';

  const [inputValue, setInputValue] = useState(urlSearch);
  const [parts, setParts] = useState<Part[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Controlled by URL params
  const executedSearch = urlSearch;
  const currentPage = urlPage;
  const pageSize = urlPageSize;
  const includeInactives = urlIncludeInactives;

  // Update URL params helper
  const updateUrlParams = useCallback(
    (updates: {
      search?: string;
      page?: number;
      pageSize?: number;
      includeInactives?: boolean;
    }) => {
      const newParams = new URLSearchParams(searchParams);

      if (updates.search !== undefined) {
        if (updates.search) {
          newParams.set('search', updates.search);
        } else {
          newParams.delete('search');
        }
      }
      if (updates.page !== undefined) {
        if (updates.page > 1) {
          newParams.set('page', updates.page.toString());
        } else {
          newParams.delete('page');
        }
      }
      if (updates.pageSize !== undefined) {
        if (updates.pageSize !== 50) {
          newParams.set('pageSize', updates.pageSize.toString());
        } else {
          newParams.delete('pageSize');
        }
      }
      if (updates.includeInactives !== undefined) {
        if (updates.includeInactives) {
          newParams.set('includeInactives', 'true');
        } else {
          newParams.delete('includeInactives');
        }
      }

      setSearchParams(newParams, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  // Cache key based on search params
  const cacheKey = `parts_${executedSearch}_${currentPage}_${pageSize}_${includeInactives}`;

  // Fetch parts when URL params change (and there's a search term)
  useEffect(() => {
    const fetchParts = async () => {
      if (!executedSearch) {
        setParts([]);
        setTotalPages(0);
        setTotalCount(0);
        return;
      }

      // Check cache first
      const cached = getCachedData<{
        parts: Part[];
        totalPages: number;
        totalCount: number;
      }>(cacheKey);

      if (cached) {
        setParts(cached.parts);
        setTotalPages(cached.totalPages);
        setTotalCount(cached.totalCount);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await apiService.getParts(
          executedSearch,
          currentPage,
          pageSize,
          includeInactives
        );
        setParts(response.parts);
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.total);

        // Cache the result
        setCachedData(cacheKey, {
          parts: response.parts,
          totalPages: response.pagination.totalPages,
          totalCount: response.pagination.total,
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch parts';
        setError(errorMessage);
        console.error('Error fetching parts:', err);
        setParts([]);
        setTotalPages(0);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchParts();
  }, [executedSearch, currentPage, pageSize, includeInactives, cacheKey]);

  const handleSearch = () => {
    const trimmedSearch = inputValue.trim();

    if (!trimmedSearch) {
      setError(t('common.error') + ': ' + t('parts.search.enterSearchTerm'));
      return;
    }

    setError(null);
    // Clear cache to force fresh data on new search
    clearAllCachedDataFor('parts');
    // Update URL which triggers the fetch
    updateUrlParams({
      search: trimmedSearch,
      page: 1,
      pageSize,
      includeInactives,
    });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    if (!executedSearch) return;

    updateUrlParams({ page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    updateUrlParams({ pageSize: newSize, page: 1 });
  };

  const handleIncludeInactivesChange = (checked: boolean) => {
    // Update URL params - this will trigger a new search if there's an active search
    if (executedSearch) {
      updateUrlParams({ includeInactives: checked, page: 1 });
    } else {
      // No active search, just update the checkbox state via URL
      updateUrlParams({ includeInactives: checked });
    }
  };

  const columns: ColumnDef<Part>[] = [
    {
      key: 'part',
      header: t('parts.search.partKey'),
      className: 'font-mono',
      render: (_, part) => {
        const partValue = (part as unknown as Part).part || '-';
        const erpValue = (part as unknown as Part).erpPartNum || '-';
        return `${partValue} (${erpValue})`;
      },
    },
    {
      key: 'descriptionEN',
      header: t('parts.search.descriptionEN'),
      render: (value) => (
        <span className="inline-block max-w-xs truncate">
          {String(value || '-')}
        </span>
      ),
    },
    {
      key: 'descriptionFR',
      header: t('parts.search.descriptionFR'),
      render: (value) => (
        <span className="inline-block max-w-xs truncate">
          {String(value || '-')}
        </span>
      ),
    },
    {
      key: '_action',
      header: t('parts.search.action'),
      render: (_, part) => {
        const partNum =
          (part as unknown as Part).erpPartNum ||
          (part as unknown as Part).part;
        return (
          <div className="inline-flex items-center gap-2">
            <Link
              to={`/parts/${partNum}`}
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-9 px-3 bg-blue-600 text-white hover:bg-blue-700"
            >
              <Eye className="w-4 h-4" />
              {t('parts.search.view')}
            </Link>
            <Link
              to={`/label-printing?type=internal-part&partNumber=${encodeURIComponent(partNum)}`}
              className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 h-9 px-3 bg-green-600 text-white hover:bg-green-700"
              title="Print label for this part"
            >
              <Printer className="w-4 h-4" />
              {t('labelPrinting.print')}
            </Link>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Search Input with Button */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t('parts.search.searchPlaceholder')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          />
        </div>
        <div className="flex gap-2 items-center">
          <label className="flex items-center gap-2 px-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeInactives}
              onChange={(e) => handleIncludeInactivesChange(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-700 whitespace-nowrap">
              {t('parts.search.includeInactives', 'Include Inactive')}
            </span>
          </label>
          <button
            onClick={handleSearch}
            disabled={isLoading || !inputValue.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors whitespace-nowrap"
          >
            {isLoading ? t('parts.search.searching') : t('common.search')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-900">Error</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            {t('parts.search.searching')}...
          </p>
        </div>
      )}

      {/* Results Info and Page Size Selector */}
      {!error && totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-600">
            {t('parts.search.showingPage', {
              page: currentPage,
              totalPages: totalPages,
              total: totalCount,
              search: executedSearch
                ? t('parts.search.matchingSearch', { search: executedSearch })
                : '',
            })}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">
              {t('common.filter')}:
            </label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              disabled={isLoading}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}

      {/* Results Table */}
      {!error && parts.length > 0 && !isLoading ? (
        <div className="space-y-4">
          {/* Top Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                {t('parts.search.pageOf', { page: currentPage, totalPages })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className="flex items-center gap-1"
                >
                  {t('common.next')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <DataTable
            columns={columns}
            data={parts}
            keyExtractor={(part) => part.part}
            isEmpty={parts.length === 0}
            emptyMessage="No parts found"
          />

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-sm text-gray-600">
                {t('parts.search.pageOf', { page: currentPage, totalPages })}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className="flex items-center gap-1"
                >
                  {t('common.next')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Initial State - Prompting for Search */}
      {!error && totalCount === 0 && !isLoading && !executedSearch && (
        <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
          <Search className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <p className="text-blue-900 font-medium">
            {t('parts.search.enterSearchTerm')}
          </p>
          <p className="text-blue-700 text-sm mt-1">
            {t('parts.search.searchInstructions')}
          </p>
        </div>
      )}

      {/* No Results State - Search Performed but No Results */}
      {!error && totalCount === 0 && !isLoading && executedSearch && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t('parts.search.noResultsFound')}</p>
          <p className="text-gray-400 text-sm mt-1">
            {t('parts.search.tryDifferentSearch')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PartsSearch;
